import { useState } from 'react'
import type { Tcg } from '../../types'

/** Human-readable label per TCG. */
const TCG_LABEL: Record<Tcg, string> = {
  mtg: 'Magic: The Gathering',
  yugioh: 'Yu-Gi-Oh!',
  onepiece: 'One Piece',
  lorcana: 'Disney Lorcana',
  riftbound: 'Riftbound',
}

interface TcgHelp {
  /** Websites whose deck URLs can be imported directly. */
  urlSites: string[]
  /** Sites detected but blocked server-side (export to text instead). */
  urlBlocked?: string[]
  /** Text formats accepted in the decklist textarea. */
  textFormats: string[]
  /** A short text example. */
  example: string
}

const TCG_HELP: Record<Tcg, TcgHelp> = {
  mtg: {
    urlSites: ['archidekt.com', 'manabox.app'],
    urlBlocked: ['moxfield.com', 'deckstats.net', 'tappedout.net'],
    textFormats: [
      'Texto plano: 4 Lightning Bolt',
      'Formato Arena: 4 Lightning Bolt (M11) 146',
      'CSV de Moxfield/TappedOut (conserva el arte: set + nº)',
    ],
    example: '4 Lightning Bolt\n2 Counterspell\n1 Black Lotus',
  },
  yugioh: {
    urlSites: ['ygoprodeck.com', 'masterduelmeta.com', 'yugiohmeta.com'],
    textFormats: [
      'Nombre exacto en inglés: 3 Dark Magician',
      'ID numérico de YGOPRODeck: 1 89631139',
      'Cadena YDKE (ydke://...) pegada como texto',
    ],
    example: '3 Dark Magician\n2 Blue-Eyes White Dragon\n1 89631139',
  },
  onepiece: {
    urlSites: ['onepiece.gg', 'deckbuilder.egmanevents.com', 'deckbuilder.cardkaizoku.com'],
    textFormats: [
      'Código de carta: 4 OP01-001',
      'Nombre exacto: 4 Monkey D. Luffy',
    ],
    example: '4 OP01-001\n4 Monkey D. Luffy\n1 OP01-003',
  },
  lorcana: {
    urlSites: ['lorcana.gg', 'inkdecks.com'],
    textFormats: [
      'Nombre exacto: 4 Ariel - On Human Legs',
      'ID de dotgg',
    ],
    example: '4 Ariel - On Human Legs\n2 Elsa - Spirit of Winter',
  },
  riftbound: {
    urlSites: ['riftbound.gg'],
    urlBlocked: ['piltoverarchive.com', 'riftmana.com', 'riftbinder.com', 'riftdex.com'],
    textFormats: [
      'Código de carta: 1 OGN-001',
      'Nombre exacto: 4 Blazing Rune',
    ],
    example: '1 OGN-001\n4 OGN-100',
  },
}

const TCG_ORDER: Tcg[] = ['mtg', 'riftbound', 'onepiece', 'yugioh', 'lorcana']

/**
 * Left-hand legend listing every TCG the app can handle. Each game is a
 * collapsible row (Magic expanded by default) explaining which deck sites can
 * be imported by URL and which text formats the decklist textarea accepts.
 */
export default function TcgHelpPanel() {
  const [open, setOpen] = useState<Record<Tcg, boolean>>({
    mtg: true,
    yugioh: false,
    onepiece: false,
    lorcana: false,
    riftbound: false,
  })

  const toggle = (t: Tcg) => setOpen((s) => ({ ...s, [t]: !s[t] }))

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="font-semibold text-base mb-1">Juegos soportados</h3>
      <p className="text-xs text-gray-500 mb-3">
        Estos TCG se pueden importar por URL o pegando la lista. Despliega cada uno para ver cómo.
      </p>
      <div className="divide-y divide-gray-100">
        {TCG_ORDER.map((t) => {
          const help = TCG_HELP[t]
          const isOpen = open[t]
          return (
            <div key={t} className="py-1">
              <button
                type="button"
                onClick={() => toggle(t)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between py-2 text-left group"
              >
                <span className="text-sm font-medium text-gray-800 group-hover:text-blue-700">
                  {TCG_LABEL[t]}
                </span>
                <span
                  className={`text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                  aria-hidden
                >
                  ▸
                </span>
              </button>

              {isOpen && (
                <div className="pb-3 pl-1 space-y-2 text-xs text-gray-600">
                  <div>
                    <p className="font-semibold text-gray-700 mb-0.5">Importar por URL</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {help.urlSites.map((s) => (
                        <li key={s} className="font-mono text-[11px]">{s}</li>
                      ))}
                    </ul>
                    {help.urlBlocked && (
                      <p className="mt-1 text-amber-700">
                        {help.urlBlocked.join(', ')} bloquean la importación automática — expórtalos
                        y pégalos como texto.
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-0.5">Formato de texto</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {help.textFormats.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                    <pre className="mt-1.5 p-2 bg-gray-50 border border-gray-100 rounded font-mono text-[11px] text-gray-600 whitespace-pre-wrap">
                      {help.example}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
